import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, AlertTriangle, Ban } from "lucide-react";
import api from "../lib/api";

/**
 * Phase 5 · Live Order Tracking Strip.
 *
 * Polls GET /api/orders/{id}/track every 6s while the order is still active
 * (not completed/cancelled). Server status is authoritative — the strip just
 * renders what the backend reports. We stop polling once the order reaches
 * a terminal state to be nice to the server.
 */
export default function LiveTrackingStrip({ orderId, pollMs = 6000 }) {
  const [tr, setTr] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/track`);
      setTr(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load tracking");
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    load();
    const timer = setInterval(() => {
      // After we've seen a terminal state, stop polling.
      setTr((cur) => {
        if (cur && (cur.cancelled || cur.board_status === "completed")) {
          clearInterval(timer);
          return cur;
        }
        load();
        return cur;
      });
    }, pollMs);
    return () => clearInterval(timer);
  }, [orderId, pollMs, load]);

  if (err) {
    return <div className="text-xs text-red-300" data-testid={`tracking-error-${orderId}`}>{err}</div>;
  }
  if (!tr) return <div className="h-6 animate-pulse rounded bg-white/5" />;

  if (tr.cancelled) {
    return (
      <div
        className="rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2 text-xs text-red-200 flex items-center gap-2"
        data-testid={`tracking-cancelled-${orderId}`}
      >
        <Ban className="w-3.5 h-3.5" />
        <span className="font-semibold">Cancelled</span>
        {tr.cancel_reason && <span className="opacity-80">· {tr.cancel_reason}</span>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid={`tracking-${orderId}`}>
      <div className="flex items-center justify-between">
        <ol className="flex items-center w-full">
          {tr.steps.map((s, i) => (
            <React.Fragment key={s.key}>
              <li className="flex items-center" data-testid={`tracking-step-${orderId}-${s.key}`}>
                <div
                  className={`relative w-6 h-6 grid place-items-center rounded-full border-2 ${
                    s.done
                      ? "bg-emerald-500 border-emerald-400 text-black"
                      : s.active
                      ? "bg-pink-500 border-pink-400 text-white animate-pulse"
                      : "bg-zinc-900 border-white/15 text-zinc-500"
                  }`}
                >
                  {s.done ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : s.active ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Circle className="w-3 h-3" />
                  )}
                </div>
              </li>
              {i < tr.steps.length - 1 && (
                <li className={`h-0.5 flex-1 mx-1 ${s.done ? "bg-emerald-400/60" : "bg-white/10"}`} />
              )}
            </React.Fragment>
          ))}
        </ol>
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-zinc-400">
          <span className="font-bold text-white">
            {(tr.steps.find((s) => s.active) || tr.steps[tr.steps.length - 1])?.label}
          </span>
          {tr.fulfillment_type !== "pickup" && <span className="text-zinc-500"> · {tr.fulfillment_type}</span>}
        </span>
        {tr.has_issue && (
          <span className="inline-flex items-center gap-1 text-amber-300" data-testid={`tracking-issue-${orderId}`}>
            <AlertTriangle className="w-3 h-3" /> Issue flagged
          </span>
        )}
        {tr.unread_messages > 0 && (
          <span className="inline-flex items-center gap-1 text-pink-300 font-bold" data-testid={`tracking-unread-${orderId}`}>
            {tr.unread_messages} new msg
          </span>
        )}
      </div>
    </div>
  );
}
